/**
 * **Pajak: kode pajak · kelas fiskal produk · batas kanal** (Imron, 2026-08-03).
 *
 * ### ⭐⭐⭐ Tiga pertanyaan berbeda, tiga tempat berbeda
 * | Pertanyaan | Jawabannya | Tab |
 * |---|---|---|
 * | pajak apa yang ADA & berapa tarifnya | `tax_code` | **Kode pajak** |
 * | outlet mana yang kena | `outlet_tax` — PB1 pajak DAERAH, ikut kota outletnya | **Kode pajak** |
 * | produk mana yang kena | `tax_group` — rokok tanpa SC, voucher tanpa apa pun | **Kelas fiskal** |
 * | kanal mana yang kena | `tax_code_channel` — takeaway tanpa SC | **Kode pajak** |
 *
 * ### ⛔⛔ TARIF TIDAK BISA DIUBAH — dan itu bukan keterbatasan
 * Menaikkan PB1 dari 10% ke 11% dengan `UPDATE` mengubah **cara laporan LAMA dihitung ulang**, dan
 * selisihnya muncul di SPT. Cara yang benar: **akhiri** kode lama pada tanggalnya, lalu **buat**
 * kode baru — pola yang sama persis dengan harga (§5).
 *
 * ⚠️ Jangan tertukar: `is_inclusive` bukan "termasuk pajak lain", melainkan **harga jual sudah
 * mengandung pajak ini**. Umum di F&B Indonesia untuk PPN, jarang untuk PB1.
 */
import {
  Alert, Card, Checkbox, Col, DatePicker, Form, Input, InputNumber, Row, Select, Space, Switch,
  Table, Tabs, Tag, Typography, message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { Outlet, OutletTax, TaxCode, TaxCodeChannel, TaxGroup, TaxGroupLink } from '../api/client';
import { canWrite } from '../api/session';
import { CrudPanel } from '../components/CrudPanel';
import { FormTabs } from '../components/FormTabs';

/** Kanal harga — enum kontrak, sama dengan layar harga. */
const CHANNELS = [
  'dine_in', 'takeaway', 'delivery_gofood', 'delivery_grab', 'delivery_shopee', 'kiosk',
] as const;

/** Rezim pajak, BUKAN nama pajak. PB1 = `local_tax` (daerah, tak dikreditkan); PPN = `vat`. */
const KINDS = ['local_tax', 'vat', 'service_charge', 'withholding'] as const;

function persen(rate: string): string {
  return `${String(Math.round(Number(rate) * 10000) / 100)}%`;
}

export function Taxes() {
  const { t } = useTranslation();
  const boleh = canWrite();

  const [kode, setKode] = useState<readonly TaxCode[]>([]);
  const [link, setLink] = useState<readonly TaxGroupLink[]>([]);
  const [kanal, setKanal] = useState<readonly TaxCodeChannel[]>([]);
  const [outlet, setOutlet] = useState<readonly Outlet[]>([]);
  const [tugas, setTugas] = useState<readonly OutletTax[]>([]);
  const [grupId, setGrupId] = useState<string | null>(null);
  const [kodeId, setKodeId] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => { void api.outlets(undefined, true).then(setOutlet).catch(() => undefined); }, []);

  const muatKode = useCallback(async (): Promise<readonly TaxCode[]> => {
    const r = await api.taxCodes();
    setKode(r);
    return r;
  }, []);

  const muatLink = useCallback(() => {
    void api.taxGroupLinks().then(setLink).catch(() => undefined);
  }, []);
  useEffect(() => { muatLink(); }, [muatLink]);

  const muatKanal = useCallback(() => {
    void api.taxChannels().then(setKanal).catch(() => undefined);
  }, []);
  useEffect(() => { muatKanal(); }, [muatKanal]);

  const muatTugas = useCallback(() => {
    void api.outletTaxes().then(setTugas).catch(() => undefined);
  }, []);
  useEffect(() => { muatTugas(); }, [muatTugas]);

  const muatGrup = useCallback(() => api.taxGroups(true), []);

  const jalankan = async (f: () => Promise<unknown>) => {
    try { await f(); } catch (e) {
      void message.error(e instanceof Error ? e.message : String(e));
    }
  };

  const ubahLink = (taxCodeId: string, on: boolean) => {
    if (grupId === null) return;
    const ada = link.find((l) => l.tax_group_id === grupId && l.tax_code_id === taxCodeId);
    void jalankan(async () => {
      if (on && ada === undefined) await api.linkTax({ taxGroupId: grupId, taxCodeId });
      else if (!on && ada !== undefined) await api.unlinkTax(ada.id);
      muatLink();
    });
  };

  const ubahKanal = (taxCodeId: string, channel: string, on: boolean) => {
    const ada = kanal.find((c) => c.tax_code_id === taxCodeId && c.channel === channel);
    void jalankan(async () => {
      if (on && ada === undefined) await api.restrictChannel({ taxCodeId, channel });
      else if (!on && ada !== undefined) await api.unrestrictChannel(ada.id);
      muatKanal();
    });
  };

  const ubahOutlet = (outletId: string, on: boolean) => {
    if (kodeId === null) return;
    const ada = tugas.find((x) => x.outlet_id === outletId && x.tax_code_id === kodeId);
    void jalankan(async () => {
      if (on && ada === undefined) {
        await api.assignOutletTax({
          outletId, taxCodeId: kodeId, validFrom: dayjs().format('YYYY-MM-DD'),
        });
      } else if (!on && ada !== undefined) {
        await api.unassignOutletTax(ada.id);
      }
      muatTugas();
    });
  };

  /* ── kolom ──────────────────────────────────────────────────────────────── */

  const kolomKode: ColumnsType<TaxCode> = [
    {
      title: t('master.code'),
      dataIndex: 'code',
      width: 140,
      render: (v: string) => <b data-testid={`taxkode-${v}`}>{v}</b>,
    },
    { title: t('master.name'), dataIndex: 'name' },
    { title: t('tax.kind'), dataIndex: 'tax_kind', width: 130, render: (v: string) => <Tag>{v}</Tag> },
    {
      title: t('tax.rate'),
      dataIndex: 'rate',
      width: 90,
      render: (v: string, k: TaxCode) => (
        <span data-testid={`tarif-${k.code}`}>{persen(v)}</span>
      ),
    },
    { title: t('tax.sequence'), dataIndex: 'sequence', width: 80 },
    {
      title: t('tax.base'),
      dataIndex: 'base_components',
      /* ⛔ `?? []` bukan kelalaian: satu kolom yang melempar akan MEMBLANKKAN seluruh konsol —
         React melepas seluruh pohon saat render melempar, dan yang terlihat pengguna adalah
         halaman putih tanpa satu pun petunjuk. Kolom yang kosong jauh lebih baik. */
      render: (v: readonly string[] | null) => (
        <Space size={4} wrap>{(v ?? []).map((c) => <Tag key={c}>{c}</Tag>)}</Space>
      ),
    },
    {
      title: t('tax.validity'),
      dataIndex: 'valid_to',
      width: 150,
      render: (v: string | null, k: TaxCode) =>
        v === null ? (
          <Tag color="green" data-testid={`berjalan-${k.code}`}>{t('master.running')}</Tag>
        ) : (
          <span data-testid={`berakhir-${k.code}`}>→ {v}</span>
        ),
    },
  ];

  const kolomGrup: ColumnsType<TaxGroup> = [
    {
      title: t('master.code'),
      dataIndex: 'code',
      width: 160,
      render: (v: string) => <b data-testid={`taxgrup-${v}`}>{v}</b>,
    },
    { title: t('master.name'), dataIndex: 'name' },
    {
      title: t('tax.codesApplied'),
      key: 'kena',
      render: (_: unknown, g: TaxGroup) => {
        const punya = link.filter((l) => l.tax_group_id === g.id);
        /*
         * ⭐⭐⭐ **Kosong SENGAJA vs kosong karena LUPA** (0078, 2026-08-25).
         *
         * Dulu keduanya terlihat sama persis — satu lencana oranye. ⛔ Padahal akibatnya sangat
         * berbeda: yang sengaja (rokok, voucher) memang tak kena pajak; yang lupa akan menerbitkan
         * bill tanpa pajak **tanpa satu pun galat**, dan itu baru ketahuan saat tutup buku.
         *
         * ⚠️ Sejak gerbang kesiapan melewati grup bertanda, tanda ini bukan hiasan: ia yang
         * membedakan "outlet boleh buka" dari "outlet ditahan sampai seseorang memeriksa".
         */
        if (punya.length === 0 && g.no_tax_intended === true) {
          return (
            <Tag color="blue" data-testid={`taxgrup-sengaja-${g.code}`}>
              {t('tax.noTaxIntended')}
            </Tag>
          );
        }
        return punya.length === 0 ? (
          /* ⚠️ Bukan galat — tapi seluruh produk di grup ini tak kena pajak apa pun, dan itu
             pernyataan besar yang tak boleh terlihat seperti baris kosong biasa. */
          <Tag color="orange" data-testid={`taxgrup-kosong-${g.code}`}>{t('tax.noCode')}</Tag>
        ) : (
          <Space size={4} wrap data-testid={`taxgrup-kode-${g.code}`}>
            {punya.map((l) => (
              <Tag key={l.id}>{kode.find((k) => k.id === l.tax_code_id)?.code ?? '?'}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: '',
      dataIndex: 'is_active',
      width: 96,
      render: (v: boolean) =>
        v ? <Tag color="green">{t('outlets.active')}</Tag> : <Tag>{t('outlets.inactive')}</Tag>,
    },
  ];

  /* ── tab 1: kode pajak ──────────────────────────────────────────────────── */

  const tabKode = (
    <Row gutter={16}>
      <Col span={15}>
        <CrudPanel<TaxCode>
          testId="taxkode"
          title={t('tax.codes')}
          subtitle={t('tax.codesSubtitle')}
          canWrite={boleh}
          newLabel={t('tax.newCode')}
          columns={kolomKode}
          fetchList={muatKode}
          reloadToken={tick}
          searchIn={(k) => `${k.code} ${k.name}`}
          formWidth={560}
          onSelect={(k) => { setKodeId(k.id); }}
          selectedId={kodeId}
          renderFields={(_f, editing) => <FieldKode editing={editing} kode={kode} />}
          toFormValues={(k) => ({
            name: k.name,
            /* ⭐ Urutan ikut terisi saat menyunting — kalau tidak, form mengirim `initialValue`
               (30) dan diam-diam MEMINDAHKAN kode yang urutannya sudah benar. */
            sequence: k.sequence,
            validTo: k.valid_to === null ? null : dayjs(k.valid_to),
          })}
          onCreate={async (v) => {
            await api.createTaxCode({
              code: String(v['code']).toUpperCase(),
              name: v['name'],
              taxKind: v['taxKind'],
              /*
               * ⭐⭐ **PECAHAN, bukan angka persen.** "10" yang dimaksud 10% adalah kesalahan
               * termahal di seluruh setelan pajak: ia lolos tipe, lolos review, dan baru terlihat
               * saat totalnya sebelas kali lipat. Layar menerima persen, kawatnya selalu pecahan.
               */
              rate: (Number(v['persen'] ?? 0) / 100).toFixed(6),
              sequence: Number(v['sequence'] ?? 10),
              baseComponents: v['baseComponents'],
              isInclusive: v['isInclusive'] === true,
              validFrom: (v['validFrom'] as dayjs.Dayjs).format('YYYY-MM-DD'),
            });
            setTick((n) => n + 1);
          }}
          onUpdate={async (id, v) => {
            /*
             * ⛔ Tarif, jenis, & dasar pengenaan TAK ikut — lihat catatan di kepala berkas: cara
             * yang benar adalah mengakhiri kode lama (`validTo`) lalu membuat yang baru.
             *
             * ⭐ **`sequence` IKUT sejak 2026-08-06.** Bedanya struktural dengan tarif:
             * `order_line_tax` menyimpan snapshot tarif & jumlahnya per baris, jadi mengubah urutan
             * **tak menyentuh satu pun bill lama** — ia cuma mengubah cara bill berikutnya dihitung.
             * Yang menjaganya tetap masuk akal mesin hitung, bukan larangan menyunting.
             */
            const sampai = v['validTo'] as dayjs.Dayjs | null;
            await api.updateMaster('tax-codes', id, {
              name: v['name'],
              sequence: Number(v['sequence']),
              validTo: sampai === null ? null : sampai.format('YYYY-MM-DD'),
            });
            setTick((n) => n + 1);
          }}
        />
      </Col>

      <Col span={9}>
        <Card size="small" title={t('tax.outlets')} data-testid="kolom-outlet">
          {kodeId === null ? (
            <Typography.Text type="secondary" data-testid="tax-pilih-kode">
              {t('tax.pickCode')}
            </Typography.Text>
          ) : (
            <>
              {/* ⚠️ PB1 adalah pajak DAERAH ⇒ tarifnya ikut kabupaten/kota outletnya. Grup lintas
                  kota berarti beberapa tarif PB1 berjalan bersamaan — dan itu normal. */}
              <Alert type="info" showIcon style={{ marginBottom: 10 }} title={t('tax.outletHint')} />
              <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                {outlet.map((o) => {
                  const on = tugas.some((x) => x.outlet_id === o.id && x.tax_code_id === kodeId);
                  return (
                    <Checkbox
                      key={o.id}
                      checked={on}
                      disabled={!boleh}
                      data-testid={`outlet-kena-${o.code}`}
                      onChange={(e) => { ubahOutlet(o.id, e.target.checked); }}
                    >
                      {o.code} · {o.name}
                    </Checkbox>
                  );
                })}
              </Space>
            </>
          )}
        </Card>

        <Card size="small" title={t('tax.channelLimit')} style={{ marginTop: 12 }}
              data-testid="kolom-kanal">
          <Alert type="warning" showIcon style={{ marginBottom: 10 }} title={t('tax.channelHint')} />
          <Table
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={[...kode]}
            columns={[
              {
                title: t('master.code'),
                dataIndex: 'code',
                render: (v: string) => <span data-testid={`kanal-baris-${v}`}>{v}</span>,
              },
              {
                title: t('tax.channels'),
                key: 'kanal',
                render: (_: unknown, k: TaxCode) => {
                  const punya = kanal.filter((c) => c.tax_code_id === k.id);
                  return (
                    <Space size={4} wrap>
                      {punya.length === 0 && (
                        <Tag color="green" data-testid={`kanal-semua-${k.code}`}>
                          {t('tax.allChannels')}
                        </Tag>
                      )}
                      {CHANNELS.map((c) => {
                        const on = punya.some((x) => x.channel === c);
                        return (
                          <Tag.CheckableTag
                            key={c}
                            checked={on}
                            data-testid={`kanal-${k.code}-${c}`}
                            onChange={(v) => { if (boleh) ubahKanal(k.id, c, v); }}
                          >
                            {c}
                          </Tag.CheckableTag>
                        );
                      })}
                    </Space>
                  );
                },
              },
            ]}
          />
        </Card>
      </Col>
    </Row>
  );

  /* ── tab 2: kelas fiskal ────────────────────────────────────────────────── */

  const tabGrup = (
    <Row gutter={16}>
      <Col span={14}>
        <CrudPanel<TaxGroup>
          testId="taxgrup"
          title={t('tax.groups')}
          subtitle={t('tax.groupsSubtitle')}
          canWrite={boleh}
          newLabel={t('tax.newGroup')}
          columns={kolomGrup}
          fetchList={muatGrup}
          reloadToken={tick}
          searchIn={(g) => `${g.code} ${g.name}`}
          /* ⚠️ Klik baris MEMILIH — kode pajaknya dicentang di panel sebelah, bukan di laci:
             daftar centang di dalam laci menyembunyikan akibatnya di balik satu klik lagi. */
          onSelect={(g) => { setGrupId(g.id); }}
          selectedId={grupId}
          renderFields={(_f, editing) => (
            <>
              {editing === null ? (
                <Form.Item
                  name="code"
                  label={t('master.code')}
                  rules={[{ required: true, pattern: /^[A-Za-z0-9_-]+$/, message: t('structure.codeRule') }]}
                  tooltip={t('structure.codeFixed')}
                >
                  <Input maxLength={40} data-testid="taxgrup-kode" />
                </Form.Item>
              ) : (
                <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t('structure.codeLocked')} />
              )}
              <Form.Item name="name" label={t('master.name')} rules={[{ required: true, min: 2 }]}>
                <Input maxLength={120} data-testid="taxgrup-nama" />
              </Form.Item>
              <Alert type="info" showIcon title={t('tax.pickCodesHint')} />
              {editing !== null && (
                <Form.Item name="isActive" label={t('outlets.status')} valuePropName="checked"
                           style={{ marginTop: 16 }}>
                  <Switch
                    checkedChildren={t('outlets.active')}
                    unCheckedChildren={t('outlets.inactive')}
                  />
                </Form.Item>
              )}
            </>
          )}
          toFormValues={(g) => ({ name: g.name, isActive: g.is_active })}
          onCreate={async (v) => {
            await api.createTaxGroup({
              code: String(v['code']).toUpperCase(),
              name: String(v['name']),
            });
          }}
          onUpdate={async (id, v, lama) => {
            await api.updateMaster('tax-groups', id, { name: v['name'] });
            if (v['isActive'] !== lama.is_active) {
              await api.setStatus('tax-groups', id, v['isActive'] === true);
            }
            setTick((n) => n + 1);
          }}
        />
      </Col>

      <Col span={10}>
        <Card size="small" title={t('tax.codesApplied')} data-testid="kolom-kena">
          {grupId === null ? (
            <Typography.Text type="secondary" data-testid="tax-pilih-grup">
              {t('tax.pickGroup')}
            </Typography.Text>
          ) : (
            <>
              {/* ⛔⛔ PPN & PB1 eksklusif — disebut SEBELUM orang mencoba, bukan sesudah ditolak. */}
              <Alert type="warning" showIcon style={{ marginBottom: 10 }} title={t('tax.doubleTax')} />
              <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                {kode.map((k) => {
                  const on = link.some((l) => l.tax_group_id === grupId && l.tax_code_id === k.id);
                  /*
                   * ⭐ Kotak yang PASTI ditolak server dimatikan di sini, dengan sebabnya. Server
                   * tetap menolaknya (itu yang menegakkan) — tapi menawarkan pilihan yang pasti
                   * gagal berarti menyuruh orang mencoba lalu menyalahkan dirinya sendiri.
                   */
                  const bentrok = rezimBentrok(k, kode, link, grupId);
                  return (
                    <Checkbox
                      key={k.id}
                      checked={on}
                      disabled={!boleh || (!on && bentrok !== null)}
                      data-testid={`kena-${k.code}`}
                      onChange={(e) => { ubahLink(k.id, e.target.checked); }}
                    >
                      {k.code} · {k.name} <Tag>{k.tax_kind}</Tag>
                      <Typography.Text type="secondary">{persen(k.rate)}</Typography.Text>
                      {!on && bentrok !== null && (
                        <Tag color="red" data-testid={`bentrok-${k.code}`}>
                          {t('tax.conflictsWith', { code: bentrok })}
                        </Tag>
                      )}
                    </Checkbox>
                  );
                })}
              </Space>
            </>
          )}
        </Card>
      </Col>
    </Row>
  );

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }} data-testid="taxes">
      <Alert type="info" showIcon title={t('tax.note')} data-testid="tax-catatan" />
      <Tabs
        defaultActiveKey="kode"
        items={[
          { key: 'kode', label: <span data-testid="tab-taxkode">{t('tax.codes')}</span>, children: tabKode },
          { key: 'grup', label: <span data-testid="tab-taxgrup">{t('tax.groups')}</span>, children: tabGrup },
        ]}
      />
    </Space>
  );
}

/**
 * Kode pajak mana yang **bentrok** dengan isi grup ini — `null` bila aman.
 *
 * ⛔ PB1 (`local_tax`) & PPN (`vat`) eksklusif. `service_charge` bukan pajak dan `withholding`
 * rezim lain; keduanya selalu boleh berdampingan.
 */
function rezimBentrok(
  k: TaxCode,
  semua: readonly TaxCode[],
  link: readonly TaxGroupLink[],
  grupId: string,
): string | null {
  if (k.tax_kind !== 'vat' && k.tax_kind !== 'local_tax') return null;
  const lawan = k.tax_kind === 'vat' ? 'local_tax' : 'vat';
  const isi = link
    .filter((l) => l.tax_group_id === grupId)
    .map((l) => semua.find((x) => x.id === l.tax_code_id))
    .filter((x): x is TaxCode => x !== undefined);
  return isi.find((x) => x.tax_kind === lawan)?.code ?? null;
}

/** Field kode pajak — komponen sungguhan supaya `Form.useWatch` menuruti rules-of-hooks. */
function FieldKode({ editing, kode }: { readonly editing: TaxCode | null; readonly kode: readonly TaxCode[] }) {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const seq = Form.useWatch('sequence', form) as number | undefined;

  if (editing !== null) {
    return (
      <>
        {/* ⛔⛔ Yang paling penting di layar ini, dan sengaja jadi kalimat pertama. */}
        <Alert type="warning" showIcon style={{ marginBottom: 16 }} title={t('tax.rateFrozen')} />
        <Form.Item name="name" label={t('master.name')} rules={[{ required: true, min: 2 }]}>
          <Input maxLength={120} data-testid="taxkode-nama" />
        </Form.Item>
        <Form.Item name="validTo" label={t('tax.endsOn')} tooltip={t('tax.endsHint')}>
          <span data-testid="taxkode-sampai">
            <DatePicker style={{ width: '100%' }} />
          </span>
        </Form.Item>
      </>
    );
  }

  /*
   * ⭐ Pilihan dasar pengenaan disusun dari kode yang SUDAH ADA dan ber-`sequence` lebih kecil.
   * Server menolak yang melanggar (`precheck`), tapi menawarkan pilihan yang pasti ditolak
   * berarti menyuruh orang mencoba lalu gagal — dan yang gagal akan mengira dirinya yang salah.
   */
  const bisaDitumpuk = kode.filter((k) => k.sequence < (seq ?? 0));

  return (
    <FormTabs
      testId="taxkode"
      items={[
        {
          key: 'identitas',
          label: t('outlets.tabIdentity'),
          names: ['code', 'name'],
          children: (
            <>
              <Form.Item
                name="code"
                label={t('master.code')}
                rules={[{ required: true, pattern: /^[A-Za-z0-9_-]+$/, message: t('structure.codeRule') }]}
                tooltip={t('structure.codeFixed')}
              >
                <Input maxLength={40} data-testid="taxkode-kode" />
              </Form.Item>
              <Form.Item name="name" label={t('master.name')} rules={[{ required: true, min: 2 }]}>
                <Input maxLength={120} data-testid="taxkode-nama" />
              </Form.Item>
            </>
          ),
        },
        {
          key: 'tarif',
          label: t('tax.tabRate'),
          names: ['taxKind', 'persen', 'sequence', 'isInclusive'],
          children: (
            <>
              <Space align="start">
                <Form.Item name="taxKind" label={t('tax.kind')} initialValue="local_tax"
                           tooltip={t('tax.kindHint')}>
                  <Select style={{ width: 190 }} options={KINDS.map((k) => ({ value: k, label: k }))} />
                </Form.Item>
                {/* ⭐⭐ Layar menerima PERSEN; kawatnya selalu pecahan berskala 6. */}
                <Form.Item name="persen" label={t('tax.rate')} rules={[{ required: true }]}
                           tooltip={t('tax.rateHint')}>
                  <InputNumber min={0} max={99.999999} addonAfter="%" style={{ width: 150 }}
                               data-testid="taxkode-tarif" />
                </Form.Item>
                <Form.Item name="sequence" label={t('tax.sequence')} initialValue={30}
                           tooltip={t('tax.sequenceHint')}>
                  <InputNumber min={1} max={999} style={{ width: 110 }} data-testid="taxkode-urutan" />
                </Form.Item>
              </Space>
              <Form.Item name="isInclusive" label={t('tax.inclusive')} valuePropName="checked"
                         tooltip={t('tax.inclusiveHint')}>
                <Switch data-testid="taxkode-inklusif" />
              </Form.Item>
            </>
          ),
        },
        {
          key: 'dasar',
          label: t('tax.tabBase'),
          names: ['baseComponents', 'validFrom'],
          children: (
            <>
              <Form.Item
                name="baseComponents"
                label={t('tax.base')}
                rules={[{ required: true, message: t('tax.baseRequired') }]}
                initialValue={['net_sales']}
                tooltip={t('tax.baseHint')}
              >
                <Checkbox.Group data-testid="taxkode-dasar">
                  <Space orientation="vertical">
                    <Checkbox value="net_sales" data-testid="dasar-net_sales">net_sales</Checkbox>
                    <Checkbox value="service_charge" data-testid="dasar-service_charge">
                      service_charge
                    </Checkbox>
                    {bisaDitumpuk.map((k) => (
                      <Checkbox key={k.id} value={`tax:${k.code}`} data-testid={`dasar-tax-${k.code}`}>
                        tax:{k.code} <Typography.Text type="secondary">({k.name})</Typography.Text>
                      </Checkbox>
                    ))}
                  </Space>
                </Checkbox.Group>
              </Form.Item>
              <Form.Item name="validFrom" label={t('tax.startsOn')} rules={[{ required: true }]}
                         initialValue={dayjs()}>
                <span data-testid="taxkode-mulai">
                  <DatePicker style={{ width: '100%' }} />
                </span>
              </Form.Item>
            </>
          ),
        },
      ]}
    />
  );
}
